import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, Grid, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert, Skeleton, Card, CardContent, LinearProgress } from '@mui/material';
import axiosInstance from '../../../../utils/axiosInstance';
import {
    IndicatorTypeLabels, TimeFrameLabels,
    OSCILLATOR_TYPES, MOVING_AVERAGE_TYPES, OTHER_INDICATOR_TYPES,
    MarketRegimeTypeLabels, MarketRegimeTypeColors,
    VolRegimeTypeLabels, VolRegimeTypeColors,
    TrendDirectionLabels, TrendDirectionColors,
    TailRiskLevelLabels, TailRiskLevelColors,
    SignalDirectionLabels, SignalDirectionColors, SignalStrengthLabels, SignalStrengthColors,
    FactorTypeLabels, FactorTypeColors,
} from '../../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';
const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString() : '-';
const formatDateTime = (val) => val ? new Date(val).toLocaleString() : '-';
const formatNumber = (val) => val != null ? Number(val).toLocaleString() : '-';

const SectionTitle = ({ children, syncing }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>{children}</Typography>
        {syncing && <Chip label="Syncing..." size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20, color: '#f59e0b', borderColor: '#f59e0b', '& .MuiChip-label': { px: 1 } }} />}
    </Box>
);
const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message || 'No data available'}</Typography></Box>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);
const SectionSkeleton = ({ rows = 3 }) => (
    <TableContainer component={Paper} variant="outlined">
        <Table size="small">
            <TableHead>
                <TableRow>
                    {[1, 2, 3].map(i => <TableCell key={i}><Skeleton width="60%" /></TableCell>)}
                </TableRow>
            </TableHead>
            <TableBody>
                {[...Array(rows)].map((_, i) => (
                    <TableRow key={i}>
                        {[1, 2, 3].map(j => <TableCell key={j}><Skeleton /></TableCell>)}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const CardSkeleton = ({ count = 2 }) => (
    <Grid container spacing={2}>
        {[...Array(count)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <CardContent sx={{ p: 2.5 }}>
                        <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: '12px', mb: 2 }} />
                        <Skeleton variant="text" width="70%" sx={{ mb: 1 }} />
                        <Skeleton variant="rounded" height={6} sx={{ borderRadius: '3px', mb: 2 }} />
                        <Skeleton variant="text" width="40%" />
                    </CardContent>
                </Card>
            </Grid>
        ))}
    </Grid>
);

// Color mapping for MUI chip colors to hex values
const chipColorToHex = {
    success: '#22c55e', error: '#ef4444', warning: '#f59e0b',
    info: '#3b82f6', primary: '#6366f1', secondary: '#8b5cf6', default: '#94a3b8',
};

const MetricBox = ({ label, value, color }) => (
    <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.7rem', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {label}
        </Typography>
        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: color || '#1e293b' }}>
            {value}
        </Typography>
    </Box>
);

const ActionChip = ({ signal }) => {
    if (!signal) return <Typography variant="body2" color="text.secondary">-</Typography>;
    const colorMap = { BUY: '#22c55e', SELL: '#ef4444', NEUTRAL: '#94a3b8' };
    return (
        <Typography variant="body2" sx={{ color: colorMap[signal] || '#94a3b8', fontWeight: 600 }}>
            {signal === 'BUY' ? 'Buy' : signal === 'SELL' ? 'Sell' : 'Neutral'}
        </Typography>
    );
};

// ── Gauge Meter Component (SVG) ──
const GaugeMeter = ({ sell, neutral, buy, title }) => {
    const total = sell + neutral + buy;
    if (total === 0) return null;

    // Calculate rating: -1 (strong sell) to +1 (strong buy)
    const rating = total > 0 ? (buy - sell) / total : 0;

    // Map rating to angle: -90° (strong sell) to +90° (strong buy)
    const needleAngle = rating * 90;

    // Determine label and color
    let label, labelColor;
    if (rating > 0.5) { label = 'Strong buy'; labelColor = '#22c55e'; }
    else if (rating > 0.1) { label = 'Buy'; labelColor = '#22c55e'; }
    else if (rating > -0.1) { label = 'Neutral'; labelColor = '#64748b'; }
    else if (rating > -0.5) { label = 'Sell'; labelColor = '#ef4444'; }
    else { label = 'Strong sell'; labelColor = '#ef4444'; }

    // Gauge arc segments (5 zones): strong sell, sell, neutral, buy, strong buy
    const zones = [
        { start: -90, end: -54, color: '#ef4444' },   // strong sell
        { start: -54, end: -18, color: '#f87171' },   // sell
        { start: -18, end: 18, color: '#d1d5db' },    // neutral
        { start: 18, end: 54, color: '#86efac' },      // buy
        { start: 54, end: 90, color: '#22c55e' },      // strong buy
    ];

    const cx = 100, cy = 90, r = 70;
    const describeArc = (startAngle, endAngle) => {
        const s = (startAngle - 90) * Math.PI / 180;
        const e = (endAngle - 90) * Math.PI / 180;
        const x1 = cx + r * Math.cos(s), y1 = cy + r * Math.sin(s);
        const x2 = cx + r * Math.cos(e), y2 = cy + r * Math.sin(e);
        const large = endAngle - startAngle > 180 ? 1 : 0;
        return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
    };

    const needleRad = (needleAngle - 90) * Math.PI / 180;
    const nx = cx + (r - 15) * Math.cos(needleRad);
    const ny = cy + (r - 15) * Math.sin(needleRad);

    return (
        <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" fontWeight={700} fontSize="0.85rem" sx={{ mb: 1 }}>{title}</Typography>
            <svg width="200" height="125" viewBox="0 0 200 125">
                {/* Zone arcs */}
                {zones.map((z, i) => (
                    <path key={i} d={describeArc(z.start, z.end)} fill="none" stroke={z.color} strokeWidth={8} strokeLinecap="round" opacity={0.6} />
                ))}
                {/* Zone labels */}
                <text x="12" y="85" fontSize="7" fill="#94a3b8" textAnchor="middle">Strong sell</text>
                <text x="35" y="42" fontSize="7" fill="#94a3b8" textAnchor="middle">Sell</text>
                <text x="100" y="18" fontSize="7" fill="#94a3b8" textAnchor="middle">Neutral</text>
                <text x="165" y="42" fontSize="7" fill="#94a3b8" textAnchor="middle">Buy</text>
                <text x="188" y="85" fontSize="7" fill="#94a3b8" textAnchor="middle">Strong buy</text>
                {/* Needle */}
                <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#1e293b" strokeWidth={2} strokeLinecap="round" />
                <circle cx={cx} cy={cy} r={4} fill="#1e293b" />
            </svg>
            <Typography variant="body1" fontWeight={700} sx={{ color: labelColor, mt: -1, fontSize: '0.95rem' }}>{label}</Typography>
            {/* Sell / Neutral / Buy counts */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1 }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Sell</Typography>
                    <Typography variant="body2" fontWeight={700} color="#ef4444">{sell}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Neutral</Typography>
                    <Typography variant="body2" fontWeight={700}>{neutral}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Buy</Typography>
                    <Typography variant="body2" fontWeight={700} color="#22c55e">{buy}</Typography>
                </Box>
            </Box>
        </Box>
    );
};

// Timeframe tabs for the summary
const SUMMARY_TIMEFRAMES = [
    { key: 'ONE_MIN', label: '1 minute' },
    { key: 'FIVE_MIN', label: '5 minutes' },
    { key: 'FIFTEEN_MIN', label: '15 minutes' },
    { key: 'ONE_HOUR', label: '1 hour' },
    { key: 'TWO_HOUR', label: '2 hours' },
    { key: 'FOUR_HOUR', label: '4 hours' },
    { key: 'DAILY', label: '1 day' },
    { key: 'WEEKLY', label: '1 week' },
    { key: 'MONTHLY', label: '1 month' },
];

// Indicator table used for both Oscillators and Moving Averages columns
const IndicatorTable = ({ title, indicators }) => (
    <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, fontSize: '0.95rem' }}>
            {title} <Typography component="span" variant="caption" color="text.secondary">&rsaquo;</Typography>
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', py: 1 } }}>
                        <TableCell>Name</TableCell>
                        <TableCell align="right">Value</TableCell>
                        <TableCell align="right">Action</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {indicators.length === 0 ? (
                        <TableRow><TableCell colSpan={3} align="center"><Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>No data</Typography></TableCell></TableRow>
                    ) : indicators.map((row, i) => (
                        <TableRow key={i} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '& .MuiTableCell-root': { py: 1, borderBottom: '1px solid #f1f5f9' } }}>
                            <TableCell>
                                <Typography variant="body2" sx={{ color: '#3b82f6', fontWeight: 500, fontSize: '0.85rem' }}>
                                    {row.name || IndicatorTypeLabels[row.indicator_type] || row.indicator_type}
                                    {row.parameters && Object.keys(row.parameters).length > 0 && (
                                        <Typography component="span" variant="caption" color="text.secondary">
                                            {' '}({Object.values(row.parameters).join(', ')})
                                        </Typography>
                                    )}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" fontWeight={500} fontSize="0.85rem">
                                    {row.latest_value != null ? Number(row.latest_value).toFixed(2) : '-'}
                                </Typography>
                            </TableCell>
                            <TableCell align="right"><ActionChip signal={row.latest_signal} /></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);

const TechnicalsTab = ({ data, loading, error, symbolId }) => {
    const [summaryTimeframe, setSummaryTimeframe] = useState('DAILY');
    const [liveData, setLiveData] = useState(null);
    const pollRef = useRef(null);
    const pollCountRef = useRef(0);

    const activeData = liveData || data;
    const aiSyncing = activeData?.ai_syncing === true;

    // Auto-poll technicals when AI analysis is still syncing (max 30 polls = ~5 min)
    useEffect(() => {
        if (!symbolId || !aiSyncing || pollCountRef.current >= 30) {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            return;
        }
        if (pollRef.current) return;
        pollRef.current = setInterval(async () => {
            pollCountRef.current += 1;
            try {
                const res = await axiosInstance.get(`/api/stock-trading/symbol-detail/${symbolId}/technicals`);
                if (res.data.success) {
                    setLiveData(res.data.data);
                    if (!res.data.data.ai_syncing || pollCountRef.current >= 30) {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                    }
                }
            } catch (e) { /* ignore polling errors */ }
        }, 10000);
        return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    }, [symbolId, aiSyncing]);

    // Reset liveData and poll count when symbolId changes
    useEffect(() => { setLiveData(null); pollCountRef.current = 0; }, [symbolId]);

    if (loading) return <TabLoading />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!activeData) return <TabLoading />;

    const indicators = activeData.indicators || [];
    const regimeDetections = activeData.regime_detections || [];
    const volRegimes = activeData.vol_regimes || [];
    const trendReversions = activeData.trend_reversions || [];
    const tailRisks = activeData.tail_risks || [];
    const signals = activeData.signals || [];
    const factorScores = activeData.factor_scores || [];
    const correlations = activeData.correlations || [];

    // Filter indicators by selected timeframe
    const displayIndicators = indicators.filter(i => (i.timeframe || i.time_frame) === summaryTimeframe);

    // Split indicators into three categories
    const oscillators = displayIndicators.filter(i => OSCILLATOR_TYPES.includes(i.indicator_type));
    const movingAverages = displayIndicators.filter(i => MOVING_AVERAGE_TYPES.includes(i.indicator_type));
    const otherIndicators = displayIndicators.filter(i => OTHER_INDICATOR_TYPES.includes(i.indicator_type));
    const uncategorized = displayIndicators.filter(i => !OSCILLATOR_TYPES.includes(i.indicator_type) && !MOVING_AVERAGE_TYPES.includes(i.indicator_type) && !OTHER_INDICATOR_TYPES.includes(i.indicator_type));

    // Count signals for gauges
    const countSignals = (list) => {
        let sell = 0, neutral = 0, buy = 0;
        list.forEach(i => {
            if (!i.latest_signal) return;
            if (i.latest_signal === 'BUY') buy++;
            else if (i.latest_signal === 'SELL') sell++;
            else neutral++;
        });
        return { sell, neutral, buy };
    };

    const oscSignals = countSignals(oscillators);
    const maSignals = countSignals(movingAverages);
    const allSignals = countSignals([...oscillators, ...movingAverages, ...otherIndicators]);

    return (
        <Box>
            {/* ===== Indicators' Summary ===== */}
            <SectionTitle>Indicators' summary</SectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                See technical analysis overview for the selected timeframe. It includes key data from moving averages, oscillators, and other indicators — all summed up in the Summary gauge.
            </Typography>

            {/* Timeframe Tabs */}
            <Box sx={{ display: 'flex', gap: 0, mb: 3, flexWrap: 'wrap', border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', width: 'fit-content' }}>
                {SUMMARY_TIMEFRAMES.map(tf => (
                    <Box
                        key={tf.key}
                        onClick={() => setSummaryTimeframe(tf.key)}
                        sx={{
                            px: 2, py: 1, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500,
                            bgcolor: summaryTimeframe === tf.key ? '#1e293b' : 'transparent',
                            color: summaryTimeframe === tf.key ? '#fff' : '#64748b',
                            '&:hover': { bgcolor: summaryTimeframe === tf.key ? '#1e293b' : '#f1f5f9' },
                            borderRight: '1px solid #e2e8f0',
                            transition: 'all 0.15s',
                            userSelect: 'none',
                        }}
                    >
                        {tf.label}
                    </Box>
                ))}
            </Box>

            {/* Gauge Meters */}
            <Paper variant="outlined" sx={{ p: 3, mb: 4, bgcolor: '#fafbfe' }}>
                <Typography variant="subtitle1" fontWeight={700} textAlign="center" sx={{ mb: 2 }}>Summary</Typography>
                <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={12} sm={4}>
                        <GaugeMeter sell={oscSignals.sell} neutral={oscSignals.neutral} buy={oscSignals.buy} title="Oscillators" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <GaugeMeter sell={allSignals.sell} neutral={allSignals.neutral} buy={allSignals.buy} title="Summary" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <GaugeMeter sell={maSignals.sell} neutral={maSignals.neutral} buy={maSignals.buy} title="Moving Averages" />
                    </Grid>
                </Grid>
            </Paper>

            {/* ===== Technical Indicators Tables ===== */}
            <SectionTitle>Technical Indicators</SectionTitle>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    {oscillators.length === 0 && aiSyncing ? (
                        <>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, fontSize: '0.95rem' }}>
                                Oscillators <Chip label="Syncing..." size="small" variant="outlined" sx={{ ml: 1, fontSize: '0.65rem', height: 20, color: '#f59e0b', borderColor: '#f59e0b', '& .MuiChip-label': { px: 1 } }} />
                            </Typography>
                            <SectionSkeleton rows={4} />
                        </>
                    ) : (
                        <IndicatorTable title="Oscillators" indicators={oscillators} />
                    )}
                </Grid>
                <Grid item xs={12} md={4}>
                    {movingAverages.length === 0 && aiSyncing ? (
                        <>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, fontSize: '0.95rem' }}>
                                Moving Averages <Chip label="Syncing..." size="small" variant="outlined" sx={{ ml: 1, fontSize: '0.65rem', height: 20, color: '#f59e0b', borderColor: '#f59e0b', '& .MuiChip-label': { px: 1 } }} />
                            </Typography>
                            <SectionSkeleton rows={4} />
                        </>
                    ) : (
                        <IndicatorTable title="Moving Averages" indicators={movingAverages} />
                    )}
                </Grid>
                <Grid item xs={12} md={4}>
                    {(otherIndicators.length > 0 || uncategorized.length > 0) ? (
                        <IndicatorTable title="Other Indicators" indicators={[...otherIndicators, ...uncategorized]} />
                    ) : aiSyncing ? (
                        <>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, fontSize: '0.95rem' }}>
                                Other Indicators <Chip label="Syncing..." size="small" variant="outlined" sx={{ ml: 1, fontSize: '0.65rem', height: 20, color: '#f59e0b', borderColor: '#f59e0b', '& .MuiChip-label': { px: 1 } }} />
                            </Typography>
                            <SectionSkeleton rows={3} />
                        </>
                    ) : (
                        <IndicatorTable title="Other Indicators" indicators={[]} />
                    )}
                </Grid>
            </Grid>

            {/* Regime Detection */}
            <SectionTitle syncing={aiSyncing && regimeDetections.length === 0}>Market Regime Detection</SectionTitle>
            {regimeDetections.length === 0 ? (aiSyncing ? <CardSkeleton count={2} /> : <EmptyState message="No regime detection data" />) : (
                <Grid container spacing={2}>
                    {regimeDetections.map((row, i) => {
                        const color = chipColorToHex[MarketRegimeTypeColors[row.detected_regime]] || '#94a3b8';
                        const prob = row.regime_probability != null ? Number(row.regime_probability) : 0;
                        return (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', borderLeft: `4px solid ${color}`, height: '100%' }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Chip label={MarketRegimeTypeLabels[row.detected_regime] || row.detected_regime} size="small" color={MarketRegimeTypeColors[row.detected_regime] || 'default'} sx={{ fontFamily, fontWeight: 600, mb: 2 }} />
                                        <Box sx={{ mb: 1.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem', fontWeight: 500 }}>Probability</Typography>
                                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color }}>{formatPercent(row.regime_probability)}</Typography>
                                            </Box>
                                            <LinearProgress variant="determinate" value={Math.min(prob, 100)} sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: color } }} />
                                        </Box>
                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.7rem' }}>Detected: {formatDate(row.detection_date)}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Vol Regime */}
            <SectionTitle syncing={aiSyncing && volRegimes.length === 0}>Volatility Regime</SectionTitle>
            {volRegimes.length === 0 ? (aiSyncing ? <CardSkeleton count={2} /> : <EmptyState message="No vol regime data" />) : (
                <Grid container spacing={2}>
                    {volRegimes.map((row, i) => {
                        const color = chipColorToHex[VolRegimeTypeColors[row.vol_regime]] || '#94a3b8';
                        const pct = row.vol_percentile != null ? Number(row.vol_percentile) : 0;
                        return (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', borderLeft: `4px solid ${color}`, height: '100%' }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Chip label={VolRegimeTypeLabels[row.vol_regime] || row.vol_regime} size="small" color={VolRegimeTypeColors[row.vol_regime] || 'default'} sx={{ fontFamily, fontWeight: 600, mb: 2 }} />
                                        <Box sx={{ mb: 1.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem', fontWeight: 500 }}>Percentile</Typography>
                                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color }}>{formatPercent(row.vol_percentile)}</Typography>
                                            </Box>
                                            <LinearProgress variant="determinate" value={Math.min(pct, 100)} sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: color } }} />
                                        </Box>
                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.7rem' }}>Classified: {formatDate(row.classification_date)}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Trend Reversion */}
            <SectionTitle syncing={aiSyncing && trendReversions.length === 0}>Trend &amp; Mean Reversion</SectionTitle>
            {trendReversions.length === 0 ? (aiSyncing ? <CardSkeleton count={2} /> : <EmptyState message="No trend reversion data" />) : (
                <Grid container spacing={2}>
                    {trendReversions.map((row, i) => {
                        const color = chipColorToHex[TrendDirectionColors[row.trend_direction]] || '#94a3b8';
                        const mrScore = row.mean_reversion_score != null ? Math.min(Math.abs(Number(row.mean_reversion_score)) * 100, 100) : 0;
                        return (
                            <Grid item xs={12} sm={6} key={i}>
                                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', borderLeft: `4px solid ${color}`, height: '100%' }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                            <Chip label={TrendDirectionLabels[row.trend_direction] || row.trend_direction} size="small" color={TrendDirectionColors[row.trend_direction] || 'default'} sx={{ fontFamily, fontWeight: 600 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.7rem' }}>{formatDate(row.detection_date)}</Typography>
                                        </Box>
                                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                                            <Grid item xs={6}>
                                                <MetricBox label="Trend Strength" value={formatNumber(row.trend_strength)} color={color} />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <MetricBox label="Mean Rev. Score" value={formatNumber(row.mean_reversion_score)} />
                                            </Grid>
                                        </Grid>
                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.7rem', fontWeight: 500 }}>Mean Reversion</Typography>
                                            </Box>
                                            <LinearProgress variant="determinate" value={mrScore} sx={{ height: 5, borderRadius: 3, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: '#6366f1' } }} />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Tail Risk */}
            <SectionTitle syncing={aiSyncing && tailRisks.length === 0}>Tail Risk</SectionTitle>
            {tailRisks.length === 0 ? (aiSyncing ? <CardSkeleton count={2} /> : <EmptyState message="No tail risk data" />) : (
                <Grid container spacing={2}>
                    {tailRisks.map((row, i) => {
                        const color = chipColorToHex[TailRiskLevelColors[row.risk_level]] || '#94a3b8';
                        return (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', borderLeft: `4px solid ${color}`, height: '100%' }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                            <Chip label={TailRiskLevelLabels[row.risk_level] || row.risk_level} size="small" color={TailRiskLevelColors[row.risk_level] || 'default'} sx={{ fontFamily, fontWeight: 600 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.7rem' }}>{formatDate(row.estimation_date)}</Typography>
                                        </Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <MetricBox label="VaR 95%" value={formatPercent(row.var_95)} color="#ef4444" />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <MetricBox label="CVaR 95%" value={formatPercent(row.cvar_95)} color="#ef4444" />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Signals */}
            <SectionTitle syncing={aiSyncing && signals.length === 0}>Signals</SectionTitle>
            {signals.length === 0 ? (aiSyncing ? <CardSkeleton count={2} /> : <EmptyState message="No signals" />) : (
                <Grid container spacing={2}>
                    {signals.map((row, i) => {
                        const dirColor = chipColorToHex[SignalDirectionColors[row.direction]] || '#94a3b8';
                        const conf = row.confidence_score != null ? Number(row.confidence_score) : 0;
                        return (
                            <Grid item xs={12} sm={6} key={i}>
                                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', borderLeft: `4px solid ${dirColor}`, height: '100%' }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Chip label={SignalDirectionLabels[row.direction] || row.direction} size="small" color={SignalDirectionColors[row.direction] || 'default'} sx={{ fontFamily, fontWeight: 600 }} />
                                            {row.strength && (
                                                <Chip label={SignalStrengthLabels[row.strength] || row.strength} size="small" variant="outlined" color={SignalStrengthColors[row.strength] || 'default'} sx={{ fontFamily, fontWeight: 500, fontSize: '0.7rem' }} />
                                            )}
                                        </Box>
                                        <Box sx={{ mb: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem', fontWeight: 500 }}>Confidence</Typography>
                                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: dirColor }}>{formatPercent(row.confidence_score)}</Typography>
                                            </Box>
                                            <LinearProgress variant="determinate" value={Math.min(conf, 100)} sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: dirColor } }} />
                                        </Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <MetricBox label="Entry Price" value={formatPrice(row.entry_price)} />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <MetricBox label="Target Price" value={formatPrice(row.target_price)} color={dirColor} />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Factor Scores */}
            <SectionTitle syncing={aiSyncing && factorScores.length === 0}>Factor Scores</SectionTitle>
            {factorScores.length === 0 ? (aiSyncing ? <CardSkeleton count={3} /> : <EmptyState message="No factor scores" />) : (
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <CardContent sx={{ p: 2.5 }}>
                        {factorScores.map((row, i) => {
                            const color = chipColorToHex[FactorTypeColors[row.factor_type]] || '#94a3b8';
                            const score = row.normalized_score != null ? Number(row.normalized_score) : 0;
                            const barValue = ((score + 1) / 2) * 100; // map -1..1 to 0..100
                            return (
                                <Box key={i} sx={{ mb: i < factorScores.length - 1 ? 2.5 : 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip label={FactorTypeLabels[row.factor_type] || row.factor_type} size="small" color={FactorTypeColors[row.factor_type] || 'default'} sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }} />
                                            {row.weight != null && (
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.7rem' }}>Weight: {formatNumber(row.weight)}</Typography>
                                            )}
                                        </Box>
                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: score >= 0 ? '#22c55e' : '#ef4444' }}>
                                            {score >= 0 ? '+' : ''}{Number(score).toFixed(3)}
                                        </Typography>
                                    </Box>
                                    <LinearProgress variant="determinate" value={Math.max(0, Math.min(barValue, 100))} sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: color } }} />
                                </Box>
                            );
                        })}
                    </CardContent>
                </Card>
            )}

            {/* Correlations */}
            <SectionTitle syncing={aiSyncing && correlations.length === 0}>Correlations</SectionTitle>
            {correlations.length === 0 ? (aiSyncing ? <CardSkeleton count={2} /> : <EmptyState message="No correlation data" />) : (
                <Grid container spacing={2}>
                    {correlations.map((row, i) => {
                        const coeff = row.correlation_coefficient != null ? Number(row.correlation_coefficient) : 0;
                        const barValue = ((coeff + 1) / 2) * 100; // map -1..1 to 0..100
                        const corrColor = coeff >= 0 ? '#22c55e' : '#ef4444';
                        return (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
                                                {row.symbol_a || '-'}
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.8rem' }}>↔</Typography>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
                                                {row.symbol_b || '-'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', color: corrColor }}>
                                                {coeff >= 0 ? '+' : ''}{coeff.toFixed(4)}
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.7rem', fontWeight: 500 }}>Correlation Coefficient</Typography>
                                        </Box>
                                        <LinearProgress variant="determinate" value={Math.max(0, Math.min(barValue, 100))} sx={{ height: 6, borderRadius: 3, bgcolor: '#fecaca', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: '#22c55e' } }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                            <Typography sx={{ fontFamily, color: '#ef4444', fontSize: '0.6rem' }}>-1</Typography>
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.6rem' }}>0</Typography>
                                            <Typography sx={{ fontFamily, color: '#22c55e', fontSize: '0.6rem' }}>+1</Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Box>
    );
};

export default TechnicalsTab;
